import { Link } from "react-router-dom";
import { ShieldCheck, Users, Award, Wrench, Phone } from "lucide-react";
import { NAP, ASSETS } from "../lib/data";
import CallToActionStrip from "../components/CallToActionStrip";
import { useSEO } from "../lib/useSEO";
import { buildBreadcrumbs } from "../lib/breadcrumbs";

const VALUES = [
  { icon: ShieldCheck, title: "Licensed & Insured", body: "Fully licensed, bonded, and insured in Illinois — your home and our work are protected on every visit." },
  { icon: Users, title: "Family-Run Since 1999", body: "Three generations of Chicago appliance specialists. Same family. Same standards. Zero shortcuts." },
  { icon: Award, title: "Factory-Trained Techs", body: "Our technicians are certified on 50+ brands including Sub-Zero, Wolf, Viking, Thermador and Smeg." },
  { icon: Wrench, title: "Parts On The Truck", body: "We stock the most common parts in every van — most repairs are completed on the first visit." },
];

export default function About() {
  useSEO({
    title: "About Act Fast Appliance Repair — 26+ Years Serving Chicago",
    description:
      "Family-run Chicago appliance repair company since 1999. Licensed, insured, and factory-trained on 50+ brands including Sub-Zero, Wolf, Viking and Thermador.",
    path: "/about",
    jsonLd: buildBreadcrumbs([
      { name: "Home", path: "/" },
      { name: "About", path: "/about" },
    ]),
  });
  return (
    <div className="bg-black">
      <section className="relative pt-32 pb-16 lg:pt-44 lg:pb-24 border-b border-[#262626]">
        <div className="absolute inset-0 af-grid-noise opacity-40" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <p className="af-eyebrow mb-4">About Act Fast</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold font-display leading-[0.95] mb-6">
            26+ YEARS REPAIRING <br />
            CHICAGO'S <span className="text-[#DC2626]">KITCHENS</span> & <span className="text-[#DC2626]">LAUNDRY ROOMS</span>.
          </h1>
          <p className="text-lg text-neutral-300 max-w-3xl leading-relaxed">
            Act Fast Appliance Repair is a family-run, Chicago-based appliance service company founded in 1999.
            What started as a single van and a phone book listing has grown into a 34-community operation,
            5,000+ happy customers strong — still answered by the same family.
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 border border-[#262626] bg-[#0a0a0a] p-3">
            <img src={ASSETS.section3Van} alt="Act Fast Appliance Repair service van" className="w-full h-auto" />
          </div>
          <div className="lg:col-span-6">
            <p className="af-eyebrow mb-3">Our Story</p>
            <h2 className="text-3xl sm:text-4xl font-bold font-display mb-6">
              Built On <span className="text-[#DC2626]">First-Visit Fixes</span> and Honest Pricing.
            </h2>
            <div className="space-y-4 text-neutral-300 leading-relaxed">
              <p>
                We built Act Fast around two ideas: <strong className="text-white">show up fast</strong> and{" "}
                <strong className="text-white">finish the job on the first visit</strong>. That meant stocking
                vans like miniature parts warehouses, training every technician on the entire premium-brand
                lineup, and refusing to ever quote a price after the work is done.
              </p>
              <p>
                Today, Act Fast technicians repair Sub-Zero, Wolf, Viking, Thermador, Miele, Smeg, Hisense,
                LG, Samsung, Whirlpool, KitchenAid, Bosch, GE and 30+ more brands across Chicagoland —
                same-day, with up-front pricing and a full parts and labor warranty.
              </p>
              <p>
                If your appliance is broken, we are ready. Family-run, factory-trained, and just a quick call away.
              </p>
            </div>
            <a href={NAP.phoneTel} className="af-btn-primary mt-8">
              <Phone size={18} /> {NAP.phoneDisplay}
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-[#050505] border-y border-[#262626]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <p className="af-eyebrow mb-3 text-center">What Sets Us Apart</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-display text-center mb-14">
            Built On <span className="text-[#DC2626]">Trust</span>. Run On <span className="text-[#DC2626]">Standards</span>.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <div key={v.title} className="af-card p-6">
                  <Icon size={28} className="text-[#DC2626] mb-4" />
                  <h3 className="text-lg font-bold font-display mb-2">{v.title}</h3>
                  <p className="text-sm text-neutral-400 leading-relaxed">{v.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <CallToActionStrip />
    </div>
  );
}
